import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ROLE_LABELS } from "@/constants/workflow";
import { Input } from "@/components/ui/input";

const ROLES = Object.keys(ROLE_LABELS);

/** Access: which projects a user can open and which role they hold in each one. */
export function AccessSection({ users, projects, onChanged }) {
  const [query, setQuery] = useState("");

  const call = async (fn) => {
    try {
      await fn();
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const projectsById = useMemo(
    () => new Map(projects.map((project) => [project.id, project])),
    [projects]
  );

  const visible = useMemo(() => {
    const approved = users.filter((u) => u.approval_status === "approved");
    const needle = query.trim().toLowerCase();
    const matched = !needle
      ? approved
      : approved.filter(
          (u) =>
            `${u.full_name} ${u.email}`.toLowerCase().includes(needle) ||
            u.project_ids.some((id) =>
              (projectsById.get(id)?.code || "").toLowerCase().includes(needle)
            )
        );
    // Resolve each user's projects once instead of on every render.
    return matched.map((user) => ({
      ...user,
      assignedProjects: user.project_ids.map((id) => projectsById.get(id)).filter(Boolean),
    }));
  }, [users, query, projectsById]);

  return (
    <div className="space-y-3" data-testid="admin-access-section">
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="access-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search user or project code…"
            className="h-9 w-64 border-slate-200 bg-white pl-8 text-xs"
          />
        </div>
        <p className="mono text-[10px] uppercase tracking-wider text-slate-500">
          {visible.length} users
        </p>
      </div>

      {visible.map((user) => (
        <div
          key={user.id}
          data-testid={`admin-access-${user.email}`}
          className="rounded-xl border border-slate-200 bg-white p-5"
        >
          <div className="flex flex-wrap items-baseline gap-2">
            <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
            <p className="mono text-[11px] text-slate-500">{user.email}</p>
            <p className="mono ml-auto text-[9.5px] uppercase tracking-wider text-slate-400">
              account roles: {(user.roles || []).map((r) => ROLE_LABELS[r]).join(" · ") || "none"}
            </p>
          </div>

          <p className="mono mb-2 mt-4 text-[10px] uppercase tracking-wider text-slate-500">
            Project access
          </p>
          <div className="flex flex-wrap gap-1.5">
            {projects.map((project) => (
              <button
                key={project.id}
                data-testid={`access-${project.code}-${user.email}`}
                onClick={() =>
                  call(() =>
                    api.put(`/admin/users/${user.id}/project-access`, {
                      project_ids: user.project_ids.includes(project.id)
                        ? user.project_ids.filter((id) => id !== project.id)
                        : [...user.project_ids, project.id],
                    })
                  )
                }
                className={`mono flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-wider ${
                  user.project_ids.includes(project.id)
                    ? "bg-emerald-500/18 text-emerald-700"
                    : "border border-slate-200 text-slate-500 hover:bg-slate-100"
                }`}
              >
                {user.project_ids.includes(project.id) ? <Check size={10} /> : null}
                {project.code}
              </button>
            ))}
          </div>

          {user.project_ids.length === 0 ? (
            <p className="mt-3 text-[11.5px] text-slate-500">
              No project access yet — pick a project above to assign a role in it.
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              {user.assignedProjects.map((project) => {
                  const assigned = user.project_roles?.[project.id] || [];
                  return (
                    <div
                      key={project.id}
                      data-testid={`project-roles-${project.code}-${user.email}`}
                      className="rounded-lg bg-[#EEF2F8] p-2.5"
                    >
                      <p className="mono mb-1.5 text-[9.5px] uppercase tracking-wider text-slate-600">
                        {project.code} role{" "}
                        {assigned.length === 0 ? "(falling back to user roles)" : ""}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {ROLES.map((role) => (
                          <button
                            key={role}
                            data-testid={`project-role-${role}-${project.code}-${user.email}`}
                            onClick={() =>
                              call(() =>
                                api.put(
                                  `/admin/users/${user.id}/projects/${project.id}/roles`,
                                  {
                                    roles: assigned.includes(role)
                                      ? assigned.filter((r) => r !== role)
                                      : [...assigned, role],
                                  }
                                )
                              )
                            }
                            className={`mono rounded-full px-2.5 py-0.5 text-[9.5px] uppercase tracking-wider ${
                              assigned.includes(role)
                                ? "bg-cyan-600 text-white"
                                : "border border-slate-300 bg-white text-slate-500 hover:bg-slate-100"
                            }`}
                          >
                            {ROLE_LABELS[role]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
